import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const SiteConfigContext = createContext(null);

const applyConfig = (cfg) => {
    if (!cfg) return;
    const root = document.documentElement;
    const map = {
        "--background": cfg.color_background,
        "--foreground": cfg.color_foreground,
        "--primary": cfg.color_primary,
        "--primary-foreground": cfg.color_primary_foreground,
        "--secondary": cfg.color_secondary,
        "--secondary-foreground": cfg.color_secondary_foreground,
        "--accent": cfg.color_accent,
        "--accent-foreground": cfg.color_accent_foreground,
        "--muted": cfg.color_muted,
        "--muted-foreground": cfg.color_muted_foreground,
        "--border": cfg.color_border,
        "--card": cfg.color_card,
        "--footer-bg": cfg.color_footer_bg,
        "--footer-text": cfg.color_footer_text,
    };
    Object.entries(map).forEach(([k, v]) => {
        if (v) root.style.setProperty(k, v);
    });
    if (cfg.font_heading) root.style.setProperty("--font-heading", `"${cfg.font_heading}", Georgia, serif`);
    if (cfg.font_body) root.style.setProperty("--font-body", `"${cfg.font_body}", -apple-system, BlinkMacSystemFont, sans-serif`);
    if (cfg.animation_duration) root.style.setProperty("--anim-duration", `${cfg.animation_duration}ms`);

    if (cfg.font_google_url) {
        let link = document.getElementById("yacrrd-fonts");
        if (!link) {
            link = document.createElement("link");
            link.id = "yacrrd-fonts";
            link.rel = "stylesheet";
            document.head.appendChild(link);
        }
        if (link.href !== cfg.font_google_url) link.href = cfg.font_google_url;
    }
    // SEO
    if (cfg.seo_title) document.title = cfg.seo_title;
    const setMeta = (name, content) => {
        if (!content) return;
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute("name", name);
            document.head.appendChild(el);
        }
        el.setAttribute("content", content);
    };
    setMeta("description", cfg.seo_description);
    setMeta("keywords", cfg.seo_keywords);

    // Favicon (site icon) — falls back to shipped /favicon.jpg when cleared
    {
        const favSource = cfg.favicon_url && cfg.favicon_url.trim() ? cfg.favicon_url : "/favicon.jpg";
        const href = favSource.startsWith("http") || favSource.startsWith("/")
            ? favSource
            : `/api/files/${favSource}`;
        const lower = href.toLowerCase();
        const type = lower.endsWith(".png") ? "image/png"
            : lower.endsWith(".svg") ? "image/svg+xml"
            : lower.endsWith(".ico") ? "image/x-icon"
            : lower.endsWith(".webp") ? "image/webp"
            : "image/jpeg";
        let link = document.getElementById("yacrrd-favicon");
        if (!link) {
            link = document.createElement("link");
            link.id = "yacrrd-favicon";
            link.rel = "icon";
            document.head.appendChild(link);
        }
        link.type = type;
        if (link.href !== href) link.href = href;
        let apple = document.getElementById("yacrrd-apple-icon");
        if (!apple) {
            apple = document.createElement("link");
            apple.id = "yacrrd-apple-icon";
            apple.rel = "apple-touch-icon";
            document.head.appendChild(apple);
        }
        if (apple.href !== href) apple.href = href;
    }
};

export const SiteConfigProvider = ({ children }) => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        try {
            const { data } = await api.get("/config");
            setConfig(data);
            applyConfig(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return (
        <SiteConfigContext.Provider value={{ config, loading, reload, setConfig: (c) => { setConfig(c); applyConfig(c); } }}>
            {children}
        </SiteConfigContext.Provider>
    );
};

export const useSiteConfig = () => useContext(SiteConfigContext);
